/**
 * Mirrors of the backend lead enums (microservice-user-profile
 * docs/leads/01-schema.md and erd.drawio; `app/utils/leads/enums.py` once it
 * lands). Values are the wire strings; labels live in the UI layer.
 */

/** Departments with Leads access (A9). Titles match HR department titles. */
export const LEAD_DEPARTMENT = Object.freeze({
  EXECUTIVE: 'Executive',
  MARKETING: 'Marketing',
  SALES: 'Sales',
});

/** LeadWorkState.status (display order lives in ui/tones STATUS_OPTIONS). */
export const LEAD_STATUS = Object.freeze({
  NEW: 'new',
  CONTACTED: 'contacted',
  NO_ANSWER: 'no_answer',
  INTERESTED: 'interested',
  CALL_BACK: 'call_back',
  CONSULT_BOOKED: 'consult_booked',
  NOT_INTERESTED: 'not_interested',
  JUNK: 'junk',
});

/** UserProfile.account_state (N4) */
export const ACCOUNT_STATE = Object.freeze({
  REGISTERED: 'registered',
  PROSPECT: 'prospect',
  MERGED: 'merged',
  ERASED: 'erased',
});

/** LinkFormFill.attach_basis */
export const ATTACH_BASIS = Object.freeze({
  NEW_PROSPECT: 'new_prospect',
  LEAD_PHONE: 'lead_phone',
  LEAD_EMAIL: 'lead_email',
  APP_PHONE: 'app_phone',
  APP_EMAIL: 'app_email',
  VERIFIED_EMAIL_MERGE: 'verified_email_merge',
  SALES_MERGE: 'sales_merge',
});

/** UserPhoneDetails.origin / UserEmailDetails.origin */
export const CONTACT_ORIGIN = Object.freeze({ APP: 'app', LEAD_FORM: 'lead_form' });

/** LinkFormFill.user_state_at_fill */
export const USER_STATE_AT_FILL = Object.freeze({
  PROSPECT: 'prospect',
  REGISTERED_UNVERIFIED: 'registered_unverified',
  REGISTERED_FREE: 'registered_free',
  REGISTERED_PAID: 'registered_paid',
  REGISTERED_LAPSED: 'registered_lapsed',
});

/** LinkFormFill.source_kind */
export const FILL_SOURCE_KIND = Object.freeze({
  PUBLIC_FORM: 'public_form',
  STAFF_ENTRY: 'staff_entry',
  META_IMPORT: 'meta_import',
});

/** LinkFormFill.risk_flags */
export const RISK_FLAG = Object.freeze({
  JUNK_PATTERN: 'junk_pattern',
  IP_BURST: 'ip_burst',
  VISITOR_BURST: 'visitor_burst',
  FAST_FILL: 'fast_fill',
  STALE_RENDER: 'stale_render',
  BAD_SIGNATURE: 'bad_signature',
  DNC_CONTACT_MATCH: 'dnc_contact_match',
  CONTACT_MISMATCH: 'contact_mismatch',
});

/** LeadFormVersion.contact_mode / city_mode */
export const CONTACT_MODE = Object.freeze({ PHONE: 'phone', EMAIL_INSTEAD: 'email_instead' });
export const CITY_MODE = Object.freeze({ TYPED: 'typed', PICK_LIST: 'pick_list' });

/** LeadFormQuestion.field_kind */
export const FIELD_KIND = Object.freeze({
  NAME: 'name',
  PHONE: 'phone',
  EMAIL: 'email',
  GOAL: 'goal',
  CITY: 'city',
  AREA: 'area',
  LANGUAGE: 'language',
  BEST_TIME: 'best_time',
  HEIGHT: 'height',
  WEIGHT: 'weight',
  NOTES: 'notes',
  CUSTOM: 'custom',
});

/** ConsentText.purpose */
export const CONSENT_PURPOSE = Object.freeze({
  CONTACT: 'contact',
  WHATSAPP_MARKETING: 'whatsapp_marketing',
});

/** LeadSuppression.contact_kind */
export const CONTACT_KIND = Object.freeze({ PHONE: 'phone', EMAIL: 'email' });

/** LeadNote.kind */
export const NOTE_KIND = Object.freeze({ NOTE: 'note', WHATSAPP_SENT: 'whatsapp_sent' });

/** LeadChange.field */
export const LEAD_CHANGE_FIELD = Object.freeze({
  STATUS: 'status',
  ASSIGNEE: 'assignee',
  TAKE_OVER: 'take_over',
  CALLBACK_AT: 'callback_at',
  DNC: 'dnc',
  RISK_REVIEW: 'risk_review',
  JUNK: 'junk',
  POSSIBLE_MATCH: 'possible_match',
  MERGED: 'merged',
  UNMERGED: 'unmerged',
  DETACHED: 'detached',
  ERASED: 'erased',
  PII_EXPORT: 'pii_export',
  JOURNEY_STAGE: 'journey_stage',
});

/**
 * "On the app" state of a lead row (A6 c7 tags, A7c). Derived by the server
 * from account_state, open possible-match pairs and the subscriptions FDW.
 */
export const APP_STATE = Object.freeze({
  NONE: 'none',
  POSSIBLE_MATCH: 'possible_match',
  FREE: 'free',
  LAPSED: 'lapsed',
  PAYING: 'paying',
  JOINED: 'joined',
  PLAN_UNKNOWN: 'plan_unknown',
});

/**
 * Where a lead is in its subscription journey (server-derived from
 * subscriptions): Form lead → Signed up → Paid → Lapsed (→ Paid again).
 */
export const JOURNEY_STAGE = Object.freeze({
  FORM_LEAD: 'form_lead',
  SIGNED_UP: 'signed_up',
  PAID: 'paid',
  LAPSED: 'lapsed',
});

/** How a lead first entered Leads: a form fill, an unpaid app sign-up, or a lapsed membership. */
export const ENTRY_SOURCE = Object.freeze({
  FORM: 'form',
  APP_SIGNUP: 'app_signup',
  LAPSED: 'lapsed',
});

/** likely_clicks[].match_level: how sure the cross-browser guess is (never merged). */
export const MATCH_LEVEL = Object.freeze({
  HIGH: 'high',
  MEDIUM: 'medium',
});

/**
 * LeadChange.reason on `assignee` rows: why the owner changed without a
 * person doing it. null (absent) = a manual assign.
 */
export const LEAD_ASSIGN_REASON = Object.freeze({
  AUTO: 'auto',
  RETURNING: 'returning',
  ON_LEAVE: 'on_leave',
  LEFT_SALES: 'left_sales',
});

/** GET /lead-assign-settings people[].status: can this person get the next lead? */
export const ASSIGN_PERSON_STATUS = Object.freeze({
  AVAILABLE: 'available',
  OFF: 'off',
  ON_LEAVE: 'on_leave',
  OFF_SHIFT: 'off_shift',
  FULL: 'full',
});

/** POST /modify-lead-assign-settings op names. */
export const ASSIGN_SETTINGS_OP = Object.freeze({
  SET_TEAM: 'set_team',
  SET_PERSON: 'set_person',
});

/** Why a possible-match pair closed. */
export const PAIR_CLOSE_REASON = Object.freeze({
  NOT_SAME_PERSON: 'not_same_person',
  MERGED: 'merged',
  TARGET_ERASED: 'target_erased',
});

/** Plan of the app account a possible match points at (NEW-4). */
export const MATCH_TARGET_PLAN = Object.freeze({
  PAYING: 'paying',
  LAPSED: 'lapsed',
  FREE: 'free',
  UNKNOWN: 'unknown',
});

/** GET /leads `segment` values (A6 filter buttons). */
export const LEAD_SEGMENT = Object.freeze({
  MINE: 'mine',
  NEW: 'new',
  WAITING: 'waiting',
  CALLBACK_TODAY: 'callback_today',
  INTERESTED: 'interested',
  CONSULT_BOOKED: 'consult_booked',
  POSSIBLE_MATCH: 'possible_match',
  HELD: 'held',
  SIGNED_UP: 'signed_up',
  LAPSED: 'lapsed',
  PAID: 'paid',
});

/** GET /leads `sort` values (sortable columns). */
export const LEAD_SORT = Object.freeze({
  RECEIVED: 'received',
  STATUS: 'status',
  ASSIGNEE: 'assignee',
  NEXT_STEP: 'next_step',
});

export const SORT_ORDER = Object.freeze({ ASC: 'asc', DESC: 'desc' });

/** POST /modify-leads `op` values (backend LeadOpName). */
export const LEAD_OP = Object.freeze({
  SET_STATUS: 'set_status',
  ASSIGN: 'assign',
  ADD_NOTE: 'add_note',
  JUNK: 'junk',
  MERGE: 'merge',
  NOT_SAME_PERSON: 'not_same_person',
  UNMERGE: 'unmerge',
  DETACH: 'detach',
  DNC: 'dnc',
  DNC_CLEAR: 'dnc_clear',
});

/** Error codes the Leads API returns (backend LeadErrorCode). */
export const LEAD_ERROR_CODE = Object.freeze({
  STALE_LEAD: 'STALE_LEAD',
  LEAD_ERASED: 'LEAD_ERASED',
  LEAD_ASSIGNED_TO_OTHER: 'LEAD_ASSIGNED_TO_OTHER',
  LEAD_NOT_FOUND: 'LEAD_NOT_FOUND',
  LEAD_READ_ONLY: 'LEAD_READ_ONLY',
  REVERT_NOT_ALLOWED: 'REVERT_NOT_ALLOWED',
  SEARCH_TOO_SHORT: 'SEARCH_TOO_SHORT',
  FORBIDDEN_DEPARTMENT: 'FORBIDDEN_DEPARTMENT',
  RATE_LIMITED: 'RATE_LIMITED',
  DEPARTMENT_LOOKUP_UNAVAILABLE: 'DEPARTMENT_LOOKUP_UNAVAILABLE',
  CURSOR_INVALID: 'CURSOR_INVALID',
  INVALID_OP: 'INVALID_OP',
  ASSIGNEE_NOT_ALLOWED: 'ASSIGNEE_NOT_ALLOWED',
  PAIR_NOT_FOUND: 'PAIR_NOT_FOUND',
  MERGE_TARGET_INVALID: 'MERGE_TARGET_INVALID',
  NOTHING_TO_UNMERGE: 'NOTHING_TO_UNMERGE',
  FILL_NOT_FOUND: 'FILL_NOT_FOUND',
  DNC_NOT_ACTIVE: 'DNC_NOT_ACTIVE',
  DATE_RANGE_INVALID: 'DATE_RANGE_INVALID',
  DATE_RANGE_TOO_LONG: 'DATE_RANGE_TOO_LONG',
  PERSON_SET_CHANGED: 'PERSON_SET_CHANGED',
  PERSON_SET_TOO_LARGE: 'PERSON_SET_TOO_LARGE',
  CONTACT_REQUIRED: 'CONTACT_REQUIRED',
});

/** STALE_LEAD `reason`. */
export const STALE_REASON = Object.freeze({
  NEW_SUBMISSION: 'new_submission',
  STAFF_EDIT: 'staff_edit',
  PREVIEW_CHANGED: 'preview_changed',
});

/** LeadNotifyOutbox.event_type: the agents bell event type (FORGET = erase a lead's alerts). */
export const LEAD_NOTIFY_EVENT = Object.freeze({
  LEAD_ASSIGNED: 'LEAD_ASSIGNED',
  LEAD_REASSIGNED: 'LEAD_REASSIGNED',
  LEAD_STATUS: 'LEAD_STATUS',
  LEAD_ACTIVITY: 'LEAD_ACTIVITY',
  LEAD_OUTCOME: 'LEAD_OUTCOME',
  LEAD_CALLBACK: 'LEAD_CALLBACK',
  FORGET: 'FORGET',
});

/** Lead alert payload.kind: why the alert was sent. */
export const LEAD_NOTIFY_KIND = Object.freeze({
  AUTO: 'auto',
  RETURNING: 'returning',
  HANDOVER: 'handover',
  MANUAL: 'manual',
  GROUPED: 'grouped',
  TAKE_OVER: 'take_over',
  ON_LEAVE: 'on_leave',
  LEFT_SALES: 'left_sales',
  STATUS: 'status',
  PAID: 'paid',
  LAPSED: 'lapsed',
  REPEAT_FILL: 'repeat_fill',
  CONSULT_BOOKED: 'consult_booked',
  NOT_INTERESTED: 'not_interested',
  CALLBACK: 'callback',
});

/** LeadNotifyOutbox.last_error_kind: why a send failed. */
export const LEAD_NOTIFY_ERROR = Object.freeze({
  TIMEOUT: 'timeout',
  NETWORK: 'network',
  HTTP_4XX: 'http_4xx',
  HTTP_5XX: 'http_5xx',
  BAD_RESPONSE: 'bad_response',
  INVALID: 'invalid',
});
